import React, { useEffect, useState, useMemo } from 'react'

import Section from '../../../../../../components/Section'
import { Structure } from '../../../../Org/Structure/types'
import { ListFxd, ListEp } from '../../../../Org/Lists/types'
import MultilineSelect from '../../../../../../components/form/MultilineSelect'
import HoverTooltip from '../../../../../../components/HoverTooltip'

type Props = {
    regNumber: string | undefined
    posGuid: string
    posStatus: string
    structShortName: string | null
    structShortGuid: string | null
    fxdGuid: string | null
    onStructChange: (value: { structName: string; structGuid: string }) => void
    expensItemName: string | null
    onExpensItemsChange: (value: { fxdName: string; fxdGuid: string }) => void
    typeObjectPurchasePosition: string | null
    onTypeObjectPurchasePositionChange: (value: string) => void
    proposedMethodName: string | null
    onProposedMethodChange: (value: string) => void
    basisSingleSupplier: string | null
    onBasisSingleSupplierChange: (value: string) => void
    annotationPurchase: string | null
    onAnnotationPurchaseChange: (value: string) => void
    multiSelectDisabled: boolean
}

const PurchaseBasicInfo: React.FC<Props> = (props) => {
    const [structureList, setStructureList] = useState<Structure[]>([])
    const [fxdList, setFxdList] = useState<ListFxd[]>([])
    const [singleSupplierPurchaseList, setSingleSupplierPurchaseList] =
        useState<ListEp[]>([])

    const updatePosition = async (updatedField: Record<string, any>) => {
        if (props.posStatus === 'P') {
            try {
                const response = await fetch(
                    process.env.REACT_APP_API_URL +
                        '/api/backend/v2/plan_positions/' +
                        encodeURIComponent(props.posGuid) +
                        '?reg_number=' +
                        encodeURIComponent(props.regNumber as string),
                    {
                        method: 'PUT',
                        credentials: 'include',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(updatedField),
                    }
                )
                if (!response.ok) {
                    throw new Error('Ошибка обновления данных в базе')
                }
            } catch (error) {
                console.error(error)
            }
        } else {
            try {
                const response = await fetch(
                    process.env.REACT_APP_API_URL +
                        '/api/backend/v2/plan_position_draft/?reg_number=' +
                        encodeURIComponent(props.regNumber as string) +
                        '&guid=' +
                        encodeURIComponent(props.posGuid),
                    {
                        method: 'PUT',
                        credentials: 'include',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(updatedField),
                    }
                )
                if (!response.ok) {
                    throw new Error('Ошибка обновления данных в базе')
                }
            } catch (error) {
                console.error(error)
            }
        }
    }

    const handleStructChange = (value: string) => {
        const selectedStruct = structureList.find(
            (struct) => struct.guid === value
        )
        if (selectedStruct) {
            props.onStructChange({
                structGuid: selectedStruct.guid,
                structName: selectedStruct.struct_name,
            })
            updatePosition({
                struct_guid: selectedStruct.guid,
                struct_name: selectedStruct.struct_name,
            })
        }
    }

    const handleExpensItemsChange = (value: string) => {
        const selectedFxd = fxdList.find((fxd) => fxd.guid === value)
        if (selectedFxd) {
            props.onExpensItemsChange({
                fxdGuid: selectedFxd.guid,
                fxdName: selectedFxd.expense_items_name,
            })
            updatePosition({
                expense_items_guid: selectedFxd.guid,
                expense_items_name: selectedFxd.expense_items_name,
            })
        }
    }

    const handleTypeObjectChange = (value: string) => {
        props.onTypeObjectPurchasePositionChange(value)
        updatePosition({ type_object_purchase_position: value })
    }

    const handleProposedMethodChange = (value: string) => {
        props.onProposedMethodChange(value)
        updatePosition({ proposed_method_name: value })
    }

    const handleBasisSingleSupplierChange = (value: string) => {
        props.onBasisSingleSupplierChange(value)
        updatePosition({ exclusive_purchase_reason_name: value })
        updatePosition({ single_purchase_reason_name: value })
    }

    const handleAnnotationChange = (value: string) => {
        props.onAnnotationPurchaseChange(value)
        updatePosition({ annotation: value })
    }

    const getStructureList = async (): Promise<Structure[]> => {
        return fetch(
            process.env.REACT_APP_API_URL +
                '/api/backend/v1/organization_struct',
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса на способы закупок')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    const getFxdList = async (): Promise<ListFxd[]> => {
        return fetch(
            process.env.REACT_APP_API_URL +
                '/api/backend/v1/list_expense_items',
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса на способы закупок')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    const getSingleSupplierPurchase = async (): Promise<ListEp[]> => {
        return fetch(
            process.env.REACT_APP_API_URL +
                '/api/backend/v1/list_single_supplier_purchase',
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса на способы закупок')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    const selectedFxd = useMemo(
        () => fxdList.find((x) => x.guid === props.fxdGuid),
        [fxdList, props.fxdGuid]
    )
    const fxdCode = selectedFxd?.expense_items_code ?? ''

    useEffect(() => {
        const fetchAll = async () => {
            const structs = await getStructureList()
            if (structs) {
                setStructureList(structs) // Обновляем список структур
            }
            const fxds = await getFxdList()
            if (fxds) setFxdList(fxds)
            const single = await getSingleSupplierPurchase()
            if (single) {
                setSingleSupplierPurchaseList(single) // Обновляем список ед. поставщик
            }
        }
        fetchAll()
    }, [])

    let basicSingleSupplierOption = undefined
    if (props.basisSingleSupplier) {
        const selected = singleSupplierPurchaseList.find(
            (item) =>
                item.single_supplier_purchase_name == props.basisSingleSupplier
        )
        if (selected) {
            basicSingleSupplierOption = {
                value: selected.single_supplier_purchase_name,
                label: `${selected.single_supplier_purchase_paragraph}. ${selected.single_supplier_purchase_name}`,
            }
        }
    }

    return (
        <Section title="Общая информация">
            <div className="flex flex-row w-full gap-x-5 ">
                <div className="flex flex-col w-[500px] ">
                    <span className="mt-2 mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                        Подразделение
                    </span>
                    <select
                        className="form-select"
                        value={props?.structShortGuid || ''}
                        onChange={(e) => handleStructChange(e.target.value)}
                    >
                        <option disabled value="">
                            Выбрать
                        </option>
                        {structureList?.length > 0 ? (
                            structureList.map((struct, index) => (
                                <option key={index} value={struct.guid}>
                                    {struct.struct_name}
                                </option>
                            ))
                        ) : (
                            <option disabled>Данные отсутствуют</option>
                        )}
                    </select>
                </div>
                <div className="flex flex-col min-w-[200px]">
                    <span className="mt-2 mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                        Тип объекта закупки
                    </span>

                    <select
                        className="form-select "
                        value={props?.typeObjectPurchasePosition || ''}
                        onChange={(e) => handleTypeObjectChange(e.target.value)}
                    >
                        <option disabled value="">
                            Выбрать
                        </option>
                        <option value="G">Товар</option>
                        <option value="W">Работа</option>
                        <option value="S">Услуга</option>
                    </select>
                </div>
                {/* <div className="flex flex-col w-[490px]">
                    <span className="mt-2 mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                        Статья расходов
                    </span>

                    <HoverTooltip
                        tooltipText={
                            fxdCode
                                ? `${fxdCode} - ${props.expensItemName}`
                                : ''
                        }
                    >
                        <div className="w-full">
                            <MultilineSelect
                                isDisabled={false}
                                options={fxdList.map((item) => ({
                                    value: item.guid,
                                    label: `${item.expense_items_code}. ${item.expense_items_name}`,
                                }))}
                                value={{
                                    value: props.fxdGuid ? props.fxdGuid : '',
                                    label: props.expensItemName
                                        ? `${fxdCode} - ${props.expensItemName}`
                                        : '',
                                }}
                                onChange={(value) =>
                                    handleExpensItemsChange(value)
                                }
                                placeholder="Выбрать"
                                noOptionsMessage="Ничего не найдено"
                            />
                        </div>
                    </HoverTooltip>
                </div> */}
            </div>

            <div className="flex flex-col w-full ">
                <span className="mt-2 mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                    Предлагаемый способ закупки
                </span>
                <select
                    className="form-select"
                    value={props?.proposedMethodName || ''}
                    onChange={(e) => handleProposedMethodChange(e.target.value)}
                >
                    <option disabled value="">
                        Выбрать способ закупки
                    </option>
                    <option value="Закупка у единственного поставщика (исполнителя, подрядчика)">
                        Закупка у единственного поставщика (исполнителя,
                        подрядчика)
                    </option>
                    <option value="Способ закупки в соответствии с планом закупки, либо документацией о закупке">
                        Способ закупки в соответствии с планом закупки, либо
                        документацией о закупке
                    </option>
                </select>
            </div>

            {props?.proposedMethodName?.includes('единствен') && (
                <div className="flex flex-col w-full ">
                    <div className="mt-2 form-label">
                        Основание для проведения закупки у единственного
                        поставщика (исполнителя, подрядчика)
                    </div>

                    <MultilineSelect
                        isDisabled={props.multiSelectDisabled}
                        options={singleSupplierPurchaseList.map((item) => ({
                            value: item.single_supplier_purchase_name,
                            label: `${item.single_supplier_purchase_paragraph}. ${item.single_supplier_purchase_name}`,
                        }))}
                        value={basicSingleSupplierOption}
                        onChange={(value) =>
                            handleBasisSingleSupplierChange(value)
                        }
                        placeholder="Выбрать способ закупки"
                        noOptionsMessage="Ничего не найдено"
                    />
                </div>
            )}

            <div className="flex flex-col w-full ">
                <label className="mt-2 mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                    Примечание
                </label>
                <textarea
                    className="form-textarea "
                    maxLength={2000}
                    rows={3}
                    value={props?.annotationPurchase || ''}
                    onChange={(e) => handleAnnotationChange(e.target.value)}
                />
            </div>
        </Section>
    )
}

export default PurchaseBasicInfo
